import { createContext, type ComponentType } from 'react';

import type { ClaimOutcome } from '@/lib/missions/claim';
import type { CompletionStamp, EffectiveState } from '@/lib/missions/model';
import type { MissionView } from '@/types/quest';

/**
 * LA VISTA DELLA RIGA DI UNA MISSIONE che `MissionRow` passa a una riga alternativa, quando qualcuno sopra di lei ne ha messa una nel Context. Porta SOLO valori che la riga ha gia' calcolato e il gesto di oggi:
 * nessun verdetto nuovo, nessuna mutazione, nessuna scrittura nella cache.
 *
 * @param mission la riga come la manda il servizio (il titolo e' il suo, non si traduce).
 * @param title il titolo (`mission.title`).
 * @param state lo stato EFFETTIVO (`effectiveState`): una riga scaduta per orologio e' `expired` anche se il documento dice ancora `active`.
 * @param remaining i millisecondi alla scadenza, o `null` quando non scade.
 * @param ratio l'avanzamento, da 0 a 1 (`progressRatio`): 0 e' una barra vuota.
 * @param withReward il premio si mostra (`showsReward`): mai sulle righe non piu' disponibili.
 * @param essences le essenze del premio, o `null` per un premio cosmetico.
 * @param catalogRef il riferimento di catalogo del premio cosmetico, o `null`.
 * @param stamp il timbro di completamento o di riscossione, o `null` finche' il servizio non lo proietta.
 * @param notYet la missione aspetta un evento che nessuno ha ancora emesso (`showsNotYetAvailable`).
 * @param expiryLabel la frase della scadenza di oggi («Scade fra …», «Sempre disponibile», «Tempo scaduto»).
 * @param outcome l'esito della NOSTRA chiamata di riscossione, o `null`.
 * @param showClaim il bottone si mostra: la riga e' riscuotibile e nessun esito definitivo lo ha chiuso. E' l'UNICO predicato da leggere per il bottone.
 * @param isLoading la riscossione e' in volo.
 * @param onClaim il gesto di oggi, intero: la guardia contro il doppio tocco, l'aptica, la chiamata e la lettura dell'esito.
 */
export interface VistaRigaMissione {
  mission: MissionView;
  title: string;
  state: EffectiveState;
  remaining: number | null;
  ratio: number;
  withReward: boolean;
  essences: number | null;
  catalogRef: string | null;
  stamp: CompletionStamp | null;
  notYet: boolean;
  expiryLabel: string;
  outcome: ClaimOutcome | null;
  showClaim: boolean;
  isLoading: boolean;
  onClaim: () => void;
}

/**
 * IL PUNTO D'INNESTO di una riga alternativa delle missioni (stesso schema di `SlotLiveHub` e `SlotPelleCode`). Il valore di partenza e' `null`: senza nessuno sopra, `MissionRow` rende la riga di sempre e il DOM non cambia di un nodo.
 * ⛔ Il valore di partenza resta `null`: una riga di default farebbe cambiare ogni lista di missioni, anche dove nessuno l'ha scelta.
 * ⚠️ Il valore e' un componente DI MODULO: uno creato nel render cambierebbe identita' a ogni giro e smonterebbe le righe (e con loro l'esito di una riscossione appena fatta).
 */
export const SlotRigaMissione = createContext<ComponentType<{ vista: VistaRigaMissione }> | null>(null);
